import React from 'react';
import { Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material';
import DeduplicationRow from './DeduplicationRow';
import { ReferralData } from '../types';

export interface DeduplicationMatch {
  ccPatientId: string | null;
  patientMatchingScore: number;
  ccPatientReferenceId: string;
  isCareGiver: boolean;
  firstName?: string;
  lastName?: string;
  dob?: string;
  gender?: string;
  phoneNumber?: string;
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  caregiver?: {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    gender?: string;
    address?: string;
    city?: string;
    state?: string;
    zipcode?: string;
  };
  patientMatchBreakdown: {
    exactMatches: string[];
    partialMatches: string[];
  };
  dateOfBirth?: string;
  genderCode?: string;
  secondaryEmail?: {
    email: string;
  };
  primaryEmail?: {
    email: string;
  };
  address1?: string;
  zip?: string;
  id: string;
}

interface DeduplicationTableProps {
  deduplicationMatches: DeduplicationMatch[];
  onOpenMergeModal?: (match: DeduplicationMatch | undefined, row: ReferralData) => void;
  parentStatus?: string;
  selectedRecordId?: string;
  onRecordSelect?: (ccRefId: string) => void;
  hideSelectColumn?: boolean;
  fieldConfig?: Array<{ key: string; label: string }>;
  parentData?: {
    firstName?: string;
    lastName?: string;
    dob?: string;
    email?: string;
    phoneNumber?: string;
    address?: string;
    city?: string;
    state?: string;
    zipcode?: string;
    gender?: string;
    deduplication?: DeduplicationMatch[];
  };
}

const DeduplicationTable: React.FC<DeduplicationTableProps> = ({
  deduplicationMatches,
  onOpenMergeModal,
  selectedRecordId,
  onRecordSelect,
  hideSelectColumn = false,
  fieldConfig,
  parentData,
}) => {
  // Show selection column unless explicitly hidden
  const showSelectRadio = !hideSelectColumn;

  // Default columns if not provided by Sitecore
  const defaultColumns = [
    { key: 'ccPatientReferenceId', label: 'CC Ref. ID' },
    { key: 'firstName', label: 'First Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'dateOfBirth', label: 'DOB' },
    { key: 'phoneNumber', label: 'Phone Number' },
    { key: 'primaryEmail.email', label: 'Email' },
    { key: 'address1', label: 'Address' },
    { key: 'patientMatchingScore', label: 'Matching Score' },
    { key: 'gender', label: 'Gender' },
  ];

  const columns = fieldConfig && fieldConfig.length > 0 ? fieldConfig : defaultColumns;

  return (
    <Table size="small" aria-label="deduplication">
      <TableHead>
        <TableRow>
          {/* <TableCell sx={{ fontWeight: 600, width: '50px' }}></TableCell> */}
          {showSelectRadio && <TableCell sx={{ fontWeight: 600, width: '80px' }}>Select</TableCell>}
          {columns.map((column) => (
            <TableCell key={column.key} sx={{ fontWeight: 600 }}>
              {column.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {deduplicationMatches.map((match, index) => (
          <DeduplicationRow
            key={index}
            match={match}
            onOpenMergeModal={onOpenMergeModal}
            showSelectRadio={showSelectRadio}
            isSelected={selectedRecordId === match.ccPatientReferenceId}
            onSelect={() => onRecordSelect?.(match.ccPatientReferenceId)}
            columns={columns}
            parentData={parentData}
          />
        ))}
      </TableBody>
    </Table>
  );
};

export default DeduplicationTable;
